import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, CheckCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ResponsiveImage } from '../components/ResponsiveImage';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join(' | ');
  }
  return fallback;
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host.includes('youtube.com')) {
      if (parsed.pathname === '/watch') {
        const videoId = parsed.searchParams.get('v');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return url;
      }
      if (parsed.pathname.startsWith('/v/')) {
        const videoId = parsed.pathname.split('/')[2];
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.slice(1);
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    return null;
  } catch (err) {
    return null;
  }
};

export const CarDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const currentUserId = user?.id || user?._id;
  const existingReview = currentUserId
    ? reviews.find((review) => review.user_id === currentUserId)
    : null;

  const fetchCarDetails = useCallback(async () => {
    try {
      const [carRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cars/${id}`),
        axios.get(`${API_URL}/api/cars/${id}/reviews`)
      ]);
      setCar(carRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching car details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCarDetails();
  }, [fetchCarDetails]);

  useEffect(() => {
    if (!existingReview) {
      setRating(5);
      setComment('');
      return;
    }

    setRating(existingReview.rating || 5);
    setComment(existingReview.comment || '');
  }, [existingReview]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/cars/${id}/reviews`,
        { car_id: id, rating, comment },
        { withCredentials: true }
      );
      toast.success(existingReview ? 'Review updated successfully!' : 'Review submitted successfully!');
      fetchCarDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit review'));
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await axios.delete(`${API_URL}/api/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted successfully!');
      fetchCarDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete review'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Car not found</div>
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(car.video_url);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/reviews"
          className="inline-flex items-center text-[#00A859] hover:text-[#008a49] font-semibold mb-8 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Reviews
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Image Gallery */}
          <div>
            <div className="bg-white p-4 mb-4">
              <ResponsiveImage
                src={car.images[selectedImage]}
                alt={car.name}
                className="w-full h-96 object-cover"
                width={1600}
                quality={78}
                eager
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {car.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`bg-white p-2 border-2 transition-colors ${
                    selectedImage === idx ? 'border-[#00A859]' : 'border-[#E5E7EB]'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <ResponsiveImage
                    src={img}
                    alt={`${car.name} ${idx + 1}`}
                    className="w-full h-24 object-cover"
                    width={320}
                    quality={60}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Car Info */}
          <div className="bg-white p-8">
            <h1 className="text-4xl font-black mb-4 font-heading">{car.name}</h1>
            <div className="flex items-center mb-6">
              <div className="flex items-center mr-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(car.overall_rating)
                        ? 'text-[#00A859] fill-[#00A859]'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-lg text-[#666666]">
                {car.overall_rating.toFixed(1)}/5 ({reviews.length} reviews)
              </span>
            </div>
            <p className="text-4xl font-black text-[#00A859] mb-8">{car.price}</p>

            {/* Specs */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Body Type</p>
                <p className="text-lg font-bold">{car.body_type}</p>
              </div>
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Mileage</p>
                <p className="text-lg font-bold">{car.mileage}</p>
              </div>
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Fuel Type</p>
                <p className="text-lg font-bold">{car.fuel_type}</p>
              </div>
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Year</p>
                <p className="text-lg font-bold">{car.year}</p>
              </div>
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Transmission</p>
                <p className="text-lg font-bold">{car.transmission}</p>
              </div>
              <div>
                <p className="text-sm text-[#666666] uppercase tracking-wide font-semibold">Drive Type</p>
                <p className="text-lg font-bold">{car.drive_type}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6 font-heading">Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(car.features).map(([category, items]) => (
              <div key={category}>
                <h3 className="text-lg font-bold mb-3 text-[#00A859] uppercase tracking-wide">
                  {category}
                </h3>
                <ul className="space-y-2">
                  {items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <CheckCircle className="w-4 h-4 text-[#00A859] mr-2 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="bg-white p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6 font-heading">Description</h2>
          <div className="text-base leading-8 text-[#1A1A1A] whitespace-pre-line">
            {car.description?.trim()
              ? car.description
              : 'Detailed review description will be added soon.'}
          </div>
        </div>

        {/* YouTube Video */}
        {embedUrl && (
          <div className="bg-white p-8 mb-12">
            <h2 className="text-2xl font-bold mb-6 font-heading">Video Review</h2>
            <div className="aspect-video">
              <iframe
                width="100%"
                height="100%"
                src={embedUrl}
                title="Car Video Review"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        )}

        <div className="bg-white p-8 mb-12">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="text-2xl font-bold font-heading">Owner Reviews</h2>
            <span className="text-sm uppercase tracking-wide text-[#666666]">
              {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
            </span>
          </div>

          {user ? (
            <form onSubmit={handleSubmitReview} className="border border-[#E5E7EB] p-6 mb-8 space-y-4">
              <div>
                <label className="block text-sm font-semibold uppercase tracking-wide text-[#1A1A1A] mb-2">
                  Your Rating
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(value)}
                      className="transition-transform hover:scale-110"
                      aria-label={`Set rating to ${value}`}
                    >
                      <Star
                        className={`w-6 h-6 ${value <= rating ? 'text-[#00A859] fill-[#00A859]' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                  <span className="text-sm text-[#666666] ml-2">{rating}/5</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold uppercase tracking-wide text-[#1A1A1A] mb-2">
                  Your Review
                </label>
                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  rows="4"
                  required
                  placeholder="Share your experience with this car"
                  className="w-full px-4 py-3 border border-[#E5E7EB] focus:border-[#00A859] focus:ring-1 focus:ring-[#00A859]"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-6 rounded-none transition-colors"
                >
                  {existingReview ? 'Update Review' : 'Submit Review'}
                </button>
                {existingReview && (
                  <span className="text-sm text-[#666666]">Submitting again updates your existing review.</span>
                )}
              </div>
            </form>
          ) : (
            <div className="border border-[#E5E7EB] p-6 mb-8 bg-[#F8F9FA]">
              <p className="text-[#1A1A1A] mb-4">Sign in to add or update your review for this car.</p>
              <Link
                to="/login"
                className="inline-flex items-center bg-[#00A859] hover:bg-[#008a49] text-white font-bold uppercase tracking-wider py-3 px-6 rounded-none transition-colors"
              >
                Login To Review
              </Link>
            </div>
          )}

          <div className="space-y-4">
            {reviews.length ? reviews.map((review) => {
              const canDelete = user?.role === 'admin' || review.user_id === currentUserId;

              return (
                <div key={review.id} className="border border-[#E5E7EB] p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="font-bold text-[#1A1A1A]">{review.user_name}</p>
                      <p className="text-sm text-[#666666]">
                        {new Date(review.updated_at || review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, idx) => (
                          <Star
                            key={idx}
                            className={`w-4 h-4 ${idx < review.rating ? 'text-[#00A859] fill-[#00A859]' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteReview(review.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete review"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[#1A1A1A] whitespace-pre-line">{review.comment}</p>
                </div>
              );
            }) : (
              <div className="border border-dashed border-[#E5E7EB] p-6 text-[#666666]">
                No reviews yet. Be the first to share your experience.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};